<!--
  Preloading strategy:
    - preference for KISSed unique small spinner for breadcrumber only
      rather than preloader placeholders
    - then sequence of elements with "fade in effect" in a "chained display
      effect"
-->
<template>
  <div class="page-balance">

    <layout-teleport-to-breadcrumb>
      <layout-breadcrumb-loader
        :predicate="fetchState.status.value === 'success'"
      />
    </layout-teleport-to-breadcrumb>

    <transition
      v-if="fetchState.status.value === 'success'"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a1"
    >
      <div class="page-balance__filters">

        <!-- Filter: selected month -->
        <ui-buffered-container
          :is-pending="isPending"
          :idle-phase-duration="300"
          :sustain-phase-duration="300"
        >
          <template #default="{ onPreBufferPending, onBufferPending }">
            <ui-form-month-picker
              v-model="selectedMonth"
              :format="selectedMonthFormat"
              :variant="UI.VARIANT.PRIMARY"
              :m-min="Maybe.Just(currentMonth)"
              :m-max="Maybe.Just(nextDecember)"
              :status="onPreBufferPending ?
                UI.STATUS.MUTED :
                (
                  onBufferPending ?
                    UI.STATUS.DEFFERED :
                    UI.STATUS.ENABLED
                )
              "
              class="page-balance__filters__date"
            />
          </template>
        </ui-buffered-container>

        <!-- Filter: Leave Request status -->
        <ui-form-inner-icon
          class="page-balance__filters__status"
          name="label"
          :theme="UI.ICON_THEME.OUTLINED"
        >
          <ui-form-tree
            v-model="filteredLeaveRequestStatusList"
            :options="leaveRequestStatusOptions"
            placeholder="Statut de la demande"
            multiple
          >
            <!-- Input options -->
            <template #value-label="{ node }">
              <ui-badge-heavy
                :class="[
                  'page-balance__filters__status__badge',
                  `page-balance__filters__status__badge--${node.id}`
                ]"
              >
                <ui-icon
                  :name="getStatus_Icon(node.id)"
                  :theme="UI.ICON_THEME.FILLED"
                />
                {{ node.label }}
              </ui-badge-heavy>
            </template>

            <!-- Tree options -->
            <template #option-label="{ node }">
              <ui-wad class="font-size-90">
                {{ node.label }}
              </ui-wad>
            </template>
          </ui-form-tree>
        </ui-form-inner-icon>

        <!-- Filter: Collaborator or Service -->
        <ui-form-inner-icon
          class="page-balance__filters__services"
          name="filter_list"
          :theme="UI.ICON_THEME.OUTLINED"
        >
          <ui-form-tree
            v-model="selected_serviceSlug"
            :options="options_service"
            placeholder="Rechercher"
            no-children-text="Aucun salarié"
            multiple
            searchable
            clearable
            indent
            :clearable-variant="UI.VARIANT.DANGER"
          >
            <!-- Input options -->
            <template #value-label="{ node }">
              <span>{{ node.label }}</span>
            </template>

            <!-- Tree options -->
            <template #option-label="{ node }">

              <span
                v-if="node.isBranch"
              >
                {{ node.label }}
              </span>

              <span
                v-if="!node.isBranch"
                class="font-size-90"
              >
                {{ node.label }}
              </span>

            </template>
          </ui-form-tree>
        </ui-form-inner-icon>

        <!-- Export CSV -->
        <layout-cta-sub
          component="ui-button"
          :variant="UI.VARIANT.PRIMARY"
          :status="isExportPending ?
            UI.STATUS.DEFFERED :
            UI.STATUS.ENABLED
          "
          outline
          @click="exportCallback"
        >
          <ui-icon
            name="file_download"
          />
          Exporter les données
        </layout-cta-sub>

      </div>
    </transition>


    <transition
      v-if="fetchState.status.value === 'success'"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a1 d2"
    >
      <div class="page-balance__body">

        <ui-excel-grid
          ref="grid"
          style="width: 100%; height: 100%"
          :column-defs="columns"
          :suppress-cell-focus="true"
          :suppress-movable-columns="true"
          :column-hover-highlight="true"
          :default-col-def="{
            editable: false,
          }"
          :row-data="rows"
          :pinned-bottom-row-data="pinnedBottomRowData"
          :get-row-height="getRowHeight"
          :get-row-id="params => params.data.matricule"
          :animate-rows="false"
          :busy="isPending"
          hover
        />

      </div>
    </transition>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/transat/members

.page-balance
  $self: &

  flex-grow: 1
  display: flex
  flex-direction: column

  &__body
    flex-grow: 1

  /////////////////////////////////

  // empirical ideal size when all cardinality selected
  $status-filter-min-width: 275px

  &__filters
    padding: ($layout-spacer-y * 1.25) $layout-spacer-x $layout-spacer-y
    display: flex
    align-items: center
    gap: space-x(2)

    &__status
      min-width: $status-filter-min-width

      &__badge
        @include leave-request-status-badge

        // (empirical:  fitting <ui-form-tree size="sm">)
        $padding-y: 2px

        border-radius: initial // seamlessly embed the badge into the filter item
        padding-top: $padding-y
        padding-bottom: $padding-y

      /// Customise <ui-form-tree> labels / options
      :deep(.ui-form-tree)

        .vue3-treeselect__multi-value-label,
        .vue3-treeselect__multi-value-item
          padding: initial
          border: initial

    &__services
      flex-grow: 1

    /// Melting filter input with its parent container background (preventing
    /// too much affordance for these UI elements, as they are just distant
    /// setting features)
    :deep(.custom-select:not(:active):not(:focus)),
    :deep(.form-control:not(:active):not(:focus)),
    :deep(.ui-form-tree:not(.vue3-treeselect--focused) .vue3-treeselect__control)
      border-color: $gray-bright

    /// Remove focus ring ("aesthetic" choice, as filters and gutters are very
    /// tiny, we do not want a focus ring overlap)
    // @TODO: "no-focus-ring" prop?
    :deep(.custom-select:focus),
    :deep(.ui-form-input.form-control:focus),
    :deep(.ui-form-date-single .form-control:focus),
    :deep(.ui-form-tree .vue3-treeselect__control)
      box-shadow: initial

  ///////////////////////////////////////

  &__filters__date,
  &__filters__status,
  &__filters__services
    position: relative

    &::before
      $top-offset: -20px // (empirical, works well with set font-size)

      position: absolute
      top: $top-offset

      /// @TODO: SASS extend or mixin here?
      font-weight: $font-weight-bold
      font-size: 11.5px
      text-transform: uppercase
      letter-spacing: .2px
      color: $input-label-color

  &__filters__date::before
    content: "Solde estimé à fin"
  &__filters__status::before
    content: "Types de demandes"
  &__filters__services::before
    content: "Filtrer par équipes / salariés"

  ///////////////////////////////////////

  :deep(*)

    /// Column border management
    ///
    .ag-header-group-cell#{ $self }__column--top-border
      box-shadow: 0 2px 0 0 var(--ag-border-color) inset

    .ag-header-cell#{ $self }__column--right-border,
    .ag-header-group-cell#{ $self }__column--right-border
      border-right: 2px solid var(--ag-border-color)

    .ag-cell#{ $self }__column--right-border
      box-shadow: -2px 0 0 0 var(--ag-border-color) inset

      &:not(.ag-cell-focus:focus-within)
        border-right-color: var(--ag-border-color)

      /// (!) These two lines works here due to the `suppressCellFocus=false`
      ///
      ///     In a case where the flag is left as is, the below customisation
      ///     would lead to a break of the cell focus UI
      ///
      ///     See <ui-excel-grid> documentation for the entry
      ///     "entangled Cell borders with the Cursor stroke"
      ///
      border-top: initial
      border-bottom: initial

    /// Header height management
    ///
    #{ $self }__column--emphasized-header
      height: 200px

    .ag-header-cell[col-id="last_bp_month"]
      padding-left: space-x(.75)
      padding-right: space-x(.75)

      .ag-header-cell-text
        font-size: 95%
        line-height: 1.2

    .ag-header-cell[col-id^="current-paid-leave"],
    .ag-header-cell[col-id^="earned-paid-leave"],
    .ag-header-cell[col-id^="rtt-paid-leave"],
    .ag-header-cell[col-id^="compensatory-leave"]
      padding-left: space-x(.5)
      padding-right: space-x(.5)

      .ag-header-cell-text
        font-size: 87.5%
        line-height: 0.9

    /// Pinned bottom row customisation
    ///
    .ag-floating-bottom
      box-shadow: 0 0 0 2px var(--ag-border-color)

      .ag-cell
        display: flex
        align-items: center

      .ag-cell[col-id="name"]
        font-size: 105%
        font-weight: $font-weight-bolder
        background-color: var(--ag-header-background-color)
        color: var(--ag-header-foreground-color)

      .ag-cell:not([col-id="name"])
        justify-content: flex-end
        font-size: 110%
        font-weight: $font-weight-bold

    /// Header focus micro management
    ///
    .ag-grid-header-focused
      background: $gray-medium

    /// Accentuate pinned column group vertical delimitations
    ///
    .ag-center-cols-viewport
      box-shadow: -3px 0 0 0 var(--ag-border-color)

    // (patching side-effect of the above rule, otherwise cell cursor is behing
    // the add border)
    .ag-pinned-left-cols-container .ag-row
      z-index: 1

    .ag-header-cell-label .ag-header-cell-text
      // @NOTE: line clamp has not worked here,
      //    maybe use this instead [1]
      // @link https://stackoverflow.com/questions/53734697/new-line-in-header [1]
      white-space: pre-wrap
    .ag-cell-label-container
      padding: initial
      font-size: 12px

  ///////////////////////////////////////

  /// Responsive
  ///
  min-width: map-get($breakpoints, 'laptop')

</style>

<script lang="ts">
import LeaveHeader, { type Params as HeaderGroupComponentParams } from '~/components/transat/balance/excel/LeaveHeader.vue'
import Console from '~/utils/lib/core/console'
import { datetimeToDateObject, now, parseDatetime } from '~/utils/lib/core/datetime'
import { add, assoc, clone, equals, gt, includes, invokerUnit, length, lengthGte, lengthNotEq, Maybe, not, notEmpty, pathOr, pipe_, pluck, renameKey, singleton, tapCatch, toString, when } from '~/utils/lib/core/prelude'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { LEAVE_TYPE, getStatus_Icon, printStatus_, STATUS_, castToLeaveType_, type LEAVE_TYPE_ } from '~/stores/transat/leave-request'
import { useStore_transat_scheduling } from '~/stores/transat/scheduling'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useExport } from '~/components/transat/balance/excel/export.composable'
import type { UiExcelGrid } from '#components'
import type { UITreeOption } from '~/components/ui/form/Tree.vue'
import type { Service_ } from '~/stores/transat/service'
import { useStore_transat_balance, type CollaboratorHasEstimatedBalance, type EstimatedBalanceReporting } from '~/stores/transat/balance'
import type { ColDef, ColGroupDef, ColSpanParams } from 'ag-grid-community'
import { useServiceTreeselect } from '~/composables/transat/service-treeselect.composable'
import { useStore_transat_parameters_module, type LeaveModuleInterface } from '~/stores/transat/parameters/module'
import { hasFeature } from '~/composables/layout/access-control'
import { FEATURES } from '~/stores/layout/flags'
import { REMOTE_WORK_LEAVE_TYPE } from '~/stores/transat/remote-work'
import { useStore_transat_layout } from '~/stores/transat/layout'

const console = new Console('component:balance')

const TEXT_CELL_WIDTH = 200
const NUMERICAL_CELL_WIDTH = 80
const MONTH_CELL_WIDTH = 100
const TOTAL_ROW_HEIGHT = 44

type BalanceRow = CollaboratorHasEstimatedBalance & {
  name: string
  [LEAVE_TYPE.COMPENSATORY_LEAVE]?: CollaboratorHasEstimatedBalance[typeof LEAVE_TYPE.CURRENT_PAID_LEAVE]
}
type BalanceColDef<TValue = unknown> = ColDef<BalanceRow, TValue>
type BalanceColGroupDef = ColGroupDef<BalanceRow>
type BalanceColumn = ColDef<BalanceRow, any> | BalanceColGroupDef

const selectedMonthFormat = 'YYYY-MM'

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Rapports — ${navigationContext.fromCompanyLevel().structure.raison}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const ref_grid
      = useTemplateRef<InstanceType<typeof UiExcelGrid>>('grid')

    const currentMonth = now(
      {
        outputFormat: selectedMonthFormat
      }
    )

    const nextDecember = pipe_(
      currentMonth,
      datetimeToDateObject(
        {
          inputFormat: selectedMonthFormat,
        }
      ),
      invokerUnit('getFullYear'),
      add(2),
      toString,
      parseDatetime(
        {
          inputFormat: 'YYYY',
          outputFormat: selectedMonthFormat,
          additional: [ -1, 'month' ]
        },
      )
    )

    const selectedMonth = ref(currentMonth)

    const {
      isExportPending,
      exportCallback,
    } = useExport(
      ref_grid,
      selectedMonth
    )

    const serviceList = ref<Service_[]>([])
    const {
      options_service,
      selected_serviceSlug,
      selected_matriculeId,
      selected_serviceId,
      getSelectedCollaboratorGroupedByService,
    } = useServiceTreeselect(
      serviceList,
    )

    const store_transat_layout = useStore_transat_layout()
    const store_scheduling = useStore_transat_scheduling()
    const store_balance = useStore_transat_balance()
    const store_parameters_module = useStore_transat_parameters_module()

    store_transat_layout.setNavSection('balance')

    return {
      ...navigationContext,
      fetchState,
      fetchWrapper,
      ToastBuilder,
      currentMonth,
      nextDecember,
      selectedMonth,
      isExportPending,
      exportCallback,
      serviceList,
      options_service,
      selected_serviceSlug,
      selected_matriculeId,
      selected_serviceId,
      getSelectedCollaboratorGroupedByService,
      store_scheduling,
      store_balance,
      store_parameters_module,
    }
  },
  data() {
    const leaveRequestStatusOptions = [
      {
        value: STATUS_.APPROVED,
        text: printStatus_(STATUS_.APPROVED),
        isDisabled: true
      },
      {
        value: STATUS_.PENDING,
        text: printStatus_(STATUS_.PENDING)
      }
    ] as UITreeOption[]

    return {
      Maybe,
      // filters
      leaveRequestStatusOptions,
      selectedMonthFormat,
      filteredLeaveRequestStatusList: [ STATUS_.APPROVED, STATUS_.PENDING ],
      columns: [
        {
          field: 'name',
          headerName: `Salarié`,
          pinned: 'left',
          headerClass: [
            'page-balance__column--right-border',
          ],
          cellClass: [
            'page-balance__column--right-border',
          ],
          width: TEXT_CELL_WIDTH,
          sort: 'asc'
        },
        {
          field: 'team',
          headerName: `Équipe`,
          width: TEXT_CELL_WIDTH,
          colSpan: this.getTotalRowColSpan
        },
        {
          field: 'last_bp_month',
          headerName: `Dernier bulletin déposé sur Transat`,
          width: MONTH_CELL_WIDTH,
          comparator: this.last_bp_month_comparator
        },
        {
          field: 'total_leaves',
          headerName: `Total`,
          type: 'numericColumn',
          headerClass: [
            'page-balance__column--right-border',
            'ag-right-aligned-header',
          ],
          cellClass: [
            'page-balance__column--right-border',
            'ag-right-aligned-cell'
          ],
          width: NUMERICAL_CELL_WIDTH,
        },
        {
          headerValueGetter: () => this.labels![LEAVE_TYPE.CURRENT_PAID_LEAVE],
          headerClass: [
            'page-balance__column--top-border',
            'page-balance__column--right-border',
            'd-flex justify-content-center'
          ],
          headerGroupComponent: LeaveHeader,
          headerGroupComponentParams: {
            leave_type_: castToLeaveType_(LEAVE_TYPE.CURRENT_PAID_LEAVE),
            getLabel: () => this.labels![LEAVE_TYPE.CURRENT_PAID_LEAVE],
          } satisfies HeaderGroupComponentParams,
          field: LEAVE_TYPE.CURRENT_PAID_LEAVE,
          children: [
            {
              field: `${LEAVE_TYPE.CURRENT_PAID_LEAVE}.last_balance`,
              headerName: `Solde affiché sur le dernier bulletin`,
              type: 'numericColumn',
              width: NUMERICAL_CELL_WIDTH,
            },
            {
              field: `${LEAVE_TYPE.CURRENT_PAID_LEAVE}.approved`,
              headerValueGetter: this.getApprovedColHeaderName,
              type: 'numericColumn',
              width: NUMERICAL_CELL_WIDTH,
            },
            {
              field: `${LEAVE_TYPE.CURRENT_PAID_LEAVE}.estimated`,
              headerClass: [
                'page-balance__column--right-border',
                'ag-right-aligned-header',
              ],
              headerName: `Solde estimé`,
              cellClass: [
                'page-balance__column--right-border',
                'ag-right-aligned-cell'
              ],
              type: 'numericColumn',
              width: NUMERICAL_CELL_WIDTH,
            }
          ]
        },
        {
          headerValueGetter: () => this.labels![LEAVE_TYPE.EARNED_PAID_LEAVE],
          headerClass: [
            'page-balance__column--top-border',
            'page-balance__column--right-border',
            'd-flex justify-content-center'
          ],
          headerGroupComponent: LeaveHeader,
          headerGroupComponentParams: {
            leave_type_: castToLeaveType_(LEAVE_TYPE.EARNED_PAID_LEAVE),
            getLabel: () => this.labels![LEAVE_TYPE.EARNED_PAID_LEAVE]
          } satisfies HeaderGroupComponentParams,
          field: LEAVE_TYPE.EARNED_PAID_LEAVE,
          children: [
            {
              field: `${LEAVE_TYPE.EARNED_PAID_LEAVE}.last_balance`,
              headerName: `Solde affiché sur le dernier bulletin`,
              type: 'numericColumn',
              width: NUMERICAL_CELL_WIDTH,
            },
            {
              field: `${LEAVE_TYPE.EARNED_PAID_LEAVE}.to_acquire`,
              headerName: `À acquérir`,
              type: 'numericColumn',
              width: NUMERICAL_CELL_WIDTH,
            },
            {
              field: `${LEAVE_TYPE.EARNED_PAID_LEAVE}.approved`,
              headerValueGetter: this.getApprovedColHeaderName,
              type: 'numericColumn',
              width: NUMERICAL_CELL_WIDTH,
            },
            {
              field: `${LEAVE_TYPE.EARNED_PAID_LEAVE}.estimated`,
              headerClass: [
                'page-balance__column--right-border',
                'ag-right-aligned-header',
              ],
              headerName: `Solde estimé`,
              cellClass: [
                'page-balance__column--right-border',
                'ag-right-aligned-cell'
              ],
              type: 'numericColumn',
              width: NUMERICAL_CELL_WIDTH,
            }
          ]
        },
      ] as (ColDef<EstimatedBalanceReporting["rows"][number]> | ColGroupDef<EstimatedBalanceReporting["rows"][number]>)[],
      rows: [] as EstimatedBalanceReporting["rows"], // (lazy, mutates),
      totalRow: {} as EstimatedBalanceReporting["totalRow"], // (lazy, mutates)
      labels: {} as EstimatedBalanceReporting["labels"], // (lazy)
      parametersModule: null as unknown as LeaveModuleInterface, // (lazy)
      isPending: false,
    }
  },
  computed: {
    pinnedBottomRowData() {
      const name = lengthGte(2, this.rows) ?
        `${ length(this.rows) } salariés` :
        `${ length(this.rows) } salarié`

      return pipe_(
        this.totalRow,
        assoc('name', name),
        renameKey('total_leaves', 'team'), // (see `getTotalRowColSpan()`)
        singleton
      )
    }
  },
  watch: {
    selectedMonth: {
      immediate: false,
      handler() {
        const hasFetched = this.fetchState.status.value === 'success'

        if (not(hasFetched))
          return

        this.updateData()
      }
    },
    filteredLeaveRequestStatusList: {
      immediate: false,
      handler() {
        const hasFetched = this.fetchState.status.value === 'success'

        if (not(hasFetched))
          return

        this.updateData()
      },
    },
    selected_serviceSlug: {
      immediate: false,
      handler() {
        const hasFetched = this.fetchState.status.value === 'success'

        if (not(hasFetched))
          return

        this.updateData()
      }
    },
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    getStatus_Icon,
    getRowHeight(params) {
      const isPinnedRow = pipe_(
        params,
        pathOr('', [ 'node', 'rowPinned' ]),
        notEmpty
      )

      return isPinnedRow ?
        TOTAL_ROW_HEIGHT :
        undefined
    },
    getTotalRowColSpan(
      params :ColSpanParams
    ) :number {
      const isTotalRow = params.node?.rowPinned === 'bottom'

      switch (true) {

        // merging total row `Total` cell
        case isTotalRow:
          return 3

        default:
          return 1

      }
    },
    getApprovedColHeaderName() {
      // @NOTE #SP-4931: `status_List` currently on a 2 cardinality, with
      //    `STATUS_.APPROVED` always selected
      return includes(STATUS_.PENDING, this.filteredLeaveRequestStatusList) ?
        'Validées et En attente' :
        'Validées'
    },
    /**
     * @link https://www.ag-grid.com/javascript-data-grid/row-sorting/#custom-sorting
     */
    last_bp_month_comparator(
      value :CollaboratorHasEstimatedBalance["last_bp_month"],
      value_ :CollaboratorHasEstimatedBalance["last_bp_month"]
    ){
      const date = parseDatetime(
        {
          inputFormat: 'MM/YYYY',
          outputFormat: 'YYYY-MM'
        },
        value
      )
      const date_ = parseDatetime(
        {
          inputFormat: 'MM/YYYY',
          outputFormat: 'YYYY-MM'
        },
        value_
      )

      switch (true) {
        case equals(date, date_):   return 0
        case gt(date, date_):       return 1
        default:                    return -1
      }
    },
    fetch() {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return Promise.all([
        this.store_scheduling.readService(options),
        this.store_parameters_module.read(options),
      ])
      .then(([hasAccess, parametersModule]) => {
        this.serviceList = hasAccess.data
        this.parametersModule = parametersModule
      })
      .then(() => this.updateData())
      .then(() => this.buildColumns())
      .catch(tapCatch(err => {
        console.warn('fetch', err)
        this.ToastBuilder
          .error(toString(err))
          .title(`Rapports`)
          .show()
      }))
    },
    updateData() {
      // no filter set → select every service/matricule available
      const isFilterSet = lengthNotEq(0, this.selected_serviceSlug)

      const serviceList = (() => {
        if (not(isFilterSet))
          return pluck('uuidTeam')(this.serviceList)

        return this.selected_serviceId
      })()

      const matriculeList = (() => {
        if (not(isFilterSet))
          return []

        return this.selected_matriculeId
      })()

      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        month: this.selectedMonth,
        status_List: clone(this.filteredLeaveRequestStatusList),
        matriculeList,
        serviceList,
      }

      this.isPending = true

      return this.store_balance.readLeaveBalanceList(options)
        .then(results => {
            this.rows = results.rows
            this.totalRow = results.totalRow
            this.labels = results.labels
          }
        )
        .catch(err => {
          console.warn('updateData', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Rapport`)
            .show()
        })
        .finally(() => this.isPending = false)
    },
    buildColumns() {

      const rttColumn = ({
        headerValueGetter: () => this.labels![LEAVE_TYPE.RTT],
        headerGroupComponent: LeaveHeader,
        headerGroupComponentParams: {
          leave_type_: castToLeaveType_(LEAVE_TYPE.RTT),
          getLabel: () => this.labels![LEAVE_TYPE.RTT]
        },
        headerClass: [
          'page-balance__column--top-border',
          'page-balance__column--right-border',
          'd-flex justify-content-center'
        ],
        children: [
          ({
            field: `${LEAVE_TYPE.RTT}.last_balance`,
            headerName: `Solde affiché sur le dernier bulletin`,
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          }),
          ({
            field: `${LEAVE_TYPE.RTT}.to_acquire`,
            headerName: `À acquérir`,
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH
          }),
          ({
            field: `${LEAVE_TYPE.RTT}.approved`,
            headerValueGetter: this.getApprovedColHeaderName,
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          }),
          ({
            field: `${LEAVE_TYPE.RTT}.estimated`,
            headerClass: [
              'page-balance__column--right-border',
              'ag-right-aligned-header'
            ],
            headerName: `Solde estimé`,
            cellClass: [
              'page-balance__column--right-border',
              'ag-right-aligned-cell'
            ],
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          })
        ]
      })

      const rcColumn = ({
        headerValueGetter: () => this.labels![LEAVE_TYPE.COMPENSATORY_LEAVE],
        headerGroupComponent: LeaveHeader,
        headerGroupComponentParams: {
          leave_type_: castToLeaveType_(LEAVE_TYPE.COMPENSATORY_LEAVE),
          getLabel: () => this.parametersModule.rc_conversion.short_name
        } satisfies HeaderGroupComponentParams,
        headerClass: [
          'page-balance__column--top-border',
          'page-balance__column--right-border',
          'd-flex justify-content-center'
        ],
        children: [
          ({
            field: `${LEAVE_TYPE.COMPENSATORY_LEAVE}.last_balance`,
            headerName: `Solde affiché sur le dernier bulletin`,
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          }),
          ({
            field: `${LEAVE_TYPE.COMPENSATORY_LEAVE}.approved`,
            headerValueGetter: this.getApprovedColHeaderName,
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          }),
          ({
            field: `${LEAVE_TYPE.COMPENSATORY_LEAVE}.estimated`,
            headerClass: [
              'page-balance__column--right-border',
              'ag-right-aligned-header'
            ],
            headerName: `Solde estimé`,
            cellClass: [
              'page-balance__column--right-border',
              'ag-right-aligned-cell'
            ],
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          })
        ]
      })

      const remoteWorkColumn = ({
        headerValueGetter: () => this.labels![REMOTE_WORK_LEAVE_TYPE],
        headerGroupComponent: LeaveHeader,
        headerGroupComponentParams: {
          leave_type_: REMOTE_WORK_LEAVE_TYPE as unknown as LEAVE_TYPE_,
          getLabel: () => this.labels![REMOTE_WORK_LEAVE_TYPE]
        } satisfies HeaderGroupComponentParams,
        headerClass: [
          'page-balance__column--top-border',
          'page-balance__column--right-border',
          'd-flex justify-content-center'
        ],
        children: [
          ({
            field: `${REMOTE_WORK_LEAVE_TYPE}.past_months`,
            headerName: `Mois passés`,
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          }),
          ({
            field: `${REMOTE_WORK_LEAVE_TYPE}.current_month`,
            headerName: `Mois en cours`,
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          }),
          ({
            field: `${REMOTE_WORK_LEAVE_TYPE}.future_months`,
            headerClass: [
              'ag-right-aligned-header'
            ],
            headerName: `Mois à venir`,
            cellClass: [
              'ag-right-aligned-cell'
            ],
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          }),
          ({
            field: `${REMOTE_WORK_LEAVE_TYPE}.total`,
            headerClass: [
              'page-balance__column--right-border',
              'ag-right-aligned-header'
            ],
            headerName: `Total`,
            cellClass: [
              'page-balance__column--right-border',
              'ag-right-aligned-cell'
            ],
            type: 'numericColumn',
            width: NUMERICAL_CELL_WIDTH,
          })
        ]
      })


      this.columns = pipe_(
        this.columns,
        when(
          () => this.parametersModule.rtt_acquisition.allowed,
          (columns) => [ ...columns, rttColumn ]
        ),
        when(
          () => this.parametersModule.rc_conversion.allowed,
          (columns) => [ ...columns, rcColumn ]
        ),
        when(
          () => this.parametersModule.remote_work_settings?.allowed &&
            hasFeature(FEATURES.FLA_4_TELETRAVAIL),
          (columns) => [ ...columns, remoteWorkColumn ]
        ),
      )
    },
  },
})
</script>
